import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {Banner, Icon, Text} from '@albert-io/atomic';
import type {BannerType} from '@albert-io/atomic/molecules/Banner/Banner.react';
import {GuessModelV1} from '@albert-io/models';
import {type GuessCorrectness} from 'resources/augmented/Guess/GuessModel.v1';

import PartialCreditIcon from 'client/components/PartialCreditIcon/PartialCreditIcon';

import {useQuestionTranslationStore} from '../TranslationSection';

import {guessCorrectnessBanner} from '../TranslationSection/TranslationText';

export type GuessStatus = GuessCorrectness | 'unsubmitted';

interface Props {
  guess: GuessModelV1;
  partialCreditEnabled: boolean;
}

const guessCorrectnessToBannerType = new Map<GuessStatus, BannerType>([
  ['unsubmitted', 'neutral'],
  ['incorrect', 'negative'],
  ['partiallyCorrect', 'positive'],
  ['correct', 'positive']
]);

const guessCorrectnessToEnglishCopy = new Map<GuessStatus, string>([
  ['unsubmitted', 'No answer was submitted for this question.'],
  ['incorrect', 'Incorrect'],
  ['partiallyCorrect', "You've received partial credit"],
  ['correct', 'Correct']
]);

export default function GuessCorrectnessBanner({guess, partialCreditEnabled}: Props) {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  let guessStatus: GuessStatus = guess.getCorrectness?.(partialCreditEnabled);

  if (guess.getId() === '' || guess.isGuessEmpty?.()) {
    guessStatus = 'unsubmitted';
  }

  const copyMap = showTranslations ? guessCorrectnessBanner : guessCorrectnessToEnglishCopy;
  const bannerCopy = copyMap.get(guessStatus);
  const bannerType = guessCorrectnessToBannerType.get(guessStatus);

  return (
    <Banner className='u-mar-b_4' type={bannerType}>
      <div className='u-width_100pc flex justify-between'>
        <div className='u-gap_space-x2 flex items-center'>
          {['correct', 'incorrect'].includes(guessStatus) && (
            <Icon icon={guessStatus === 'correct' ? 'circle-check' : 'circle-xmark'} />
          )}
          {guessStatus === 'partiallyCorrect' && <PartialCreditIcon />}
          <Text bold color='inherit' size='m'>
            {bannerCopy}
          </Text>
        </div>
        {guessStatus === 'partiallyCorrect' && (
          <Text color='inherit' size='m'>
            {guess.getGrade().toLocaleString(undefined, {style: 'percent'})}{' '}
            {copyMap.get('correct')}
          </Text>
        )}
      </div>
    </Banner>
  );
}
